/**
 * Typed evidence contract for introduction claims.
 * A finite number never establishes publishability by itself.
 */

export type ClaimEvidenceState =
  | 'verified_live'
  | 'verified_read'
  | 'derived_same_run'
  | 'scaled_other_year'
  | 'fixture_not_revalidated'
  | 'placeholder_zero'
  | 'unresolved'
  | 'conflicting'
  | 'suppressed_by_policy'
  | 'unavailable'
  | 'applicable_not_observed'
  | 'contextual'
  | 'forbidden_source';

export type ClaimSourceKind =
  | 'shared_ghg_fetch'
  | 'population_table'
  | 'wastewater_inventory'
  | 'derived'
  | 'registry'
  | 'policy'
  | 'fixture'
  | 'none';

export interface ClaimProvenance {
  sourceKind: ClaimSourceKind;
  sourceRef: string;
  methodVersion?: string | null;
}

type ClaimBase<T> = {
  value: T | null;
  inventoryYear?: number;
  sourceYear?: number;
  qualification?: string;
  suppressionReason?: string;
  provenance: ClaimProvenance;
};

export type ClaimValue<T = number> =
  | (ClaimBase<T> & { evidenceState: 'verified_live'; value: T; inventoryYear: number })
  | (ClaimBase<T> & { evidenceState: 'verified_read'; value: T; inventoryYear: number })
  | (ClaimBase<T> & { evidenceState: 'derived_same_run'; value: T; inventoryYear: number })
  | (ClaimBase<T> & {
      evidenceState: 'scaled_other_year';
      value: T;
      inventoryYear: number;
      sourceYear: number;
      qualification: string;
    })
  | (ClaimBase<T> & { evidenceState: 'contextual'; value: T; inventoryYear: number; qualification: string })
  | (ClaimBase<T> & { evidenceState: 'applicable_not_observed'; value: T; qualification: string })
  | (ClaimBase<T> & { evidenceState: 'fixture_not_revalidated'; value: T | null; suppressionReason: string })
  | (ClaimBase<T> & { evidenceState: 'placeholder_zero'; value: 0; suppressionReason: string })
  | (ClaimBase<T> & { evidenceState: 'unresolved'; value: null; suppressionReason: string })
  | (ClaimBase<T> & { evidenceState: 'conflicting'; value: null; suppressionReason: string })
  | (ClaimBase<T> & { evidenceState: 'suppressed_by_policy'; value: T | null; suppressionReason: string })
  | (ClaimBase<T> & { evidenceState: 'unavailable'; value: null; suppressionReason: string })
  | (ClaimBase<T> & { evidenceState: 'forbidden_source'; value: null; suppressionReason: string });

const PUBLISHABLE_NUMERIC = new Set<ClaimEvidenceState>([
  'verified_live',
  'verified_read',
  'derived_same_run',
  'scaled_other_year',
]);

export function provenance(
  sourceKind: ClaimSourceKind,
  sourceRef: string,
  methodVersion?: string | null
): ClaimProvenance {
  const ref = sourceRef.trim();
  if (!sourceKind || !ref) {
    throw new Error('Claim provenance requires a source kind and a nonempty source reference');
  }
  return { sourceKind, sourceRef: ref, methodVersion: methodVersion ?? null };
}

function requireProvenance(p: ClaimProvenance): ClaimProvenance {
  return provenance(p.sourceKind, p.sourceRef, p.methodVersion);
}

export function liveClaim<T>(
  value: T,
  inventoryYear: number,
  p: ClaimProvenance,
  extras?: { sourceYear?: number; qualification?: string }
): ClaimValue<T> {
  return {
    evidenceState: 'verified_live',
    value,
    inventoryYear,
    sourceYear: extras?.sourceYear ?? inventoryYear,
    qualification: extras?.qualification,
    provenance: requireProvenance(p),
  };
}

export function readClaim<T>(
  value: T,
  inventoryYear: number,
  p: ClaimProvenance,
  extras?: { sourceYear?: number; qualification?: string }
): ClaimValue<T> {
  return {
    evidenceState: 'verified_read',
    value,
    inventoryYear,
    sourceYear: extras?.sourceYear ?? inventoryYear,
    qualification: extras?.qualification,
    provenance: requireProvenance(p),
  };
}

export function isPublishableNumeric<T>(claim: ClaimValue<T> | null | undefined): claim is ClaimValue<T> & {
  value: T;
  evidenceState: 'verified_live' | 'verified_read' | 'derived_same_run' | 'scaled_other_year';
} {
  if (!claim) return false;
  if (!PUBLISHABLE_NUMERIC.has(claim.evidenceState)) return false;
  if (claim.value == null) return false;
  if (claim.evidenceState === 'scaled_other_year') {
    return claim.sourceYear != null && Boolean(claim.qualification);
  }
  return true;
}

export function derivedSameRun<T>(
  value: T,
  inputs: Array<ClaimValue<unknown> | null | undefined>,
  inventoryYear: number,
  p: ClaimProvenance,
  extras?: { qualification?: string }
): ClaimValue<T> {
  const blocked = inputs.find((c) => !isPublishableNumeric(c));
  if (blocked) {
    return unavailableClaim(
      inventoryYear,
      requireProvenance(p),
      `derived_same_run blocked because an input is ${blocked?.evidenceState ?? 'missing'}`
    ) as ClaimValue<T>;
  }
  return {
    evidenceState: 'derived_same_run',
    value,
    inventoryYear,
    sourceYear: inventoryYear,
    qualification: extras?.qualification,
    provenance: requireProvenance(p),
  };
}

export function scaledOtherYearClaim<T>(
  value: T,
  inventoryYear: number,
  sourceYear: number,
  qualification: string,
  p: ClaimProvenance
): ClaimValue<T> {
  if (!qualification.trim()) {
    throw new Error('scaled_other_year requires a qualification');
  }
  if (!Number.isFinite(sourceYear) || sourceYear === inventoryYear) {
    throw new Error('scaled_other_year requires a sourceYear different from inventoryYear');
  }
  return {
    evidenceState: 'scaled_other_year',
    value,
    inventoryYear,
    sourceYear,
    qualification,
    provenance: requireProvenance(p),
  };
}

export function contextualClaim<T>(
  value: T,
  inventoryYear: number,
  qualification: string,
  p: ClaimProvenance
): ClaimValue<T> {
  if (!qualification.trim()) {
    throw new Error('contextual claims require a qualification');
  }
  return {
    evidenceState: 'contextual',
    value,
    inventoryYear,
    qualification,
    provenance: requireProvenance(p),
  };
}

export function applicableNotObservedClaim<T>(
  value: T,
  inventoryYear: number,
  qualification: string,
  p: ClaimProvenance,
  extras?: { suppressionReason?: string }
): ClaimValue<T> {
  return {
    evidenceState: 'applicable_not_observed',
    value,
    inventoryYear,
    qualification,
    suppressionReason: extras?.suppressionReason,
    provenance: requireProvenance(p),
  };
}

export function placeholderZeroClaim(inventoryYear: number, p: ClaimProvenance, reason: string): ClaimValue<number> {
  return {
    evidenceState: 'placeholder_zero',
    value: 0,
    inventoryYear,
    suppressionReason: reason,
    provenance: requireProvenance(p),
  };
}

export function unavailableClaim<T = number>(
  inventoryYear: number | undefined,
  p: ClaimProvenance,
  reason: string
): ClaimValue<T> {
  return {
    evidenceState: 'unavailable',
    value: null,
    inventoryYear,
    suppressionReason: reason,
    provenance: requireProvenance(p),
  };
}

export function unresolvedClaim<T = number>(inventoryYear: number | undefined, p: ClaimProvenance, reason: string): ClaimValue<T> {
  return {
    evidenceState: 'unresolved',
    value: null,
    inventoryYear,
    suppressionReason: reason,
    provenance: requireProvenance(p),
  };
}

export function conflictingClaim<T = number>(inventoryYear: number | undefined, p: ClaimProvenance, reason: string): ClaimValue<T> {
  return {
    evidenceState: 'conflicting',
    value: null,
    inventoryYear,
    suppressionReason: reason,
    provenance: requireProvenance(p),
  };
}

export function suppressedByPolicyClaim<T>(
  value: T | null,
  inventoryYear: number | undefined,
  p: ClaimProvenance,
  reason: string
): ClaimValue<T> {
  return {
    evidenceState: 'suppressed_by_policy',
    value,
    inventoryYear,
    suppressionReason: reason,
    provenance: requireProvenance(p),
  };
}

export function fixtureNotRevalidatedClaim<T>(
  value: T | null,
  inventoryYear: number | undefined,
  p: ClaimProvenance,
  reason: string
): ClaimValue<T> {
  return {
    evidenceState: 'fixture_not_revalidated',
    value,
    inventoryYear,
    suppressionReason: reason,
    provenance: requireProvenance(p),
  };
}

export function forbiddenSourceClaim<T = number>(inventoryYear: number | undefined, p: ClaimProvenance, reason: string): ClaimValue<T> {
  return {
    evidenceState: 'forbidden_source',
    value: null,
    inventoryYear,
    suppressionReason: reason,
    provenance: requireProvenance(p),
  };
}

/** Numeric member renderers must use this. Never treat placeholder zero or fixtures as live totals. */
export function numericForMemberProse<T>(claim: ClaimValue<T> | null | undefined): T | null {
  if (!isPublishableNumeric(claim)) return null;
  return claim.value;
}
